import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { gridStrongTint } from './sprintGrid';

/** How short the handle may get before it stops being something you can catch with a pointer. */
const MIN_HANDLE = 28;
/** Uneven: the right end stops at a rounded corner and needs clearance the left does not. */
const START = 2;
const END = 6;

/** A row of the table, since the browser's own bar cannot line up with the columns or bounce with them. */
const SprintScrollRail = ({
  scroller,
  /** The width of the column that stays put, which is where the track begins; none for a table without one. */
  nameWidth,
  /** Measured on, not drawn with: the resize it causes reads the box new and the table still old. */
  columns,
}: {
  scroller: React.RefObject<HTMLDivElement | null>;
  nameWidth?: string;
  columns: number;
}): JSX.Element | null => {
  /** State, not a ref: the track does not exist on the pass that first measures it. */
  const [track, setTrack] = useState<HTMLDivElement | null>(null);
  const [handle, setHandle] = useState({ overflows: false, left: 0, width: 0 });

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const span = el.scrollWidth - el.clientWidth;
    const trackWidth = track?.clientWidth ?? 0;
    const overflows = span > 0;
    let left = 0;
    let width = 0;
    if (overflows && trackWidth > 0) {
      // The handle covers the same share of the track as the screen does of the moving columns.
      const scrolling = el.scrollWidth - (el.clientWidth - trackWidth);
      const usable = trackWidth - START - END;
      width = Math.max(MIN_HANDLE, Math.round((trackWidth / scrolling) * usable));
      left = START + Math.round((el.scrollLeft / span) * (usable - width));
    }
    setHandle(prev =>
      prev.overflows === overflows && prev.left === left && prev.width === width ? prev : { overflows, left, width },
    );
  }, [scroller, track, columns]);

  /** Not on every render: a dependency-free layout effect dispatched until React gave up at fifty. */
  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    el.addEventListener('scroll', measure, { passive: true });
    // The card and the table both resize: folding a chapter or switching to euros moves every column.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    if (track) observer.observe(track);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [measure, scroller, track]);

  /** Where the grid has to be for the handle's left edge to sit at `x` px along the track. */
  const scrollFor = (x: number): number => {
    const el = scroller.current;
    const rail = track;
    if (!el || !rail) return 0;
    const travel = rail.clientWidth - START - END - handle.width;
    return travel > 0 ? ((x - START) / travel) * (el.scrollWidth - el.clientWidth) : 0;
  };

  // Held so an unmount mid-drag takes its listeners with it.
  const release = useRef<(() => void) | null>(null);
  useEffect(() => () => release.current?.(), []);

  const drag = (event: React.PointerEvent<HTMLDivElement>): void => {
    const el = scroller.current;
    if (!el) return;
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startLeft = handle.left;
    const move = (moved: PointerEvent): void => {
      el.scrollLeft = scrollFor(startLeft + moved.clientX - startX);
    };
    const done = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', done);
      // A touch taken over by the browser's own gesture never sends pointerup.
      window.removeEventListener('pointercancel', done);
      release.current = null;
    };
    release.current = done;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', done);
    window.addEventListener('pointercancel', done);
  };

  // Takes the handle to the press rather than jumping a page, which is the wrong distance here.
  const jump = (event: React.PointerEvent<HTMLDivElement>): void => {
    const el = scroller.current;
    const rail = track;
    if (!el || !rail) return;
    el.scrollLeft = scrollFor(event.clientX - rail.getBoundingClientRect().left - handle.width / 2);
  };

  if (!handle.overflows) return null;

  return (
    // A pixel taller than its band, since the line below is inside the box.
    <div aria-hidden className={cx('sticky left-0 flex h-[9px] w-full', gridStrongTint)}>
      {nameWidth && <div className="h-full shrink-0 border-r border-border" style={{ width: nameWidth }} />}
      {/* Over the track only: an open sprint tints its column red, which would otherwise meet the rail raw. */}
      <div ref={setTrack} className="relative h-full flex-1 border-t border-border" onPointerDown={jump}>
        <div
          className="absolute inset-y-[2.5px] cursor-grab rounded-[1.5px] bg-foreground/60 hover:bg-foreground active:cursor-grabbing"
          style={{ left: handle.left, width: handle.width }}
          onPointerDown={drag}
        />
      </div>
    </div>
  );
};

export default SprintScrollRail;
